import * as firebase from "firebase";
import FirebaseService from './FirebaseService';

class ChatRoomService extends FirebaseService {
    // Get a reference to a specific chat room
    static __roomReference(chatRoomId) {
        /* chatRooms/{UUID} */
        return this.safeExecute(() => this.__reference(`chatRooms/${chatRoomId}`));
    }

    // Get a reference to chat room members or a specific member
    static __membersReference(chatRoomId, memberId = null) {
        /* chatRooms/{UUID}/members/{memberId} */
        const referencePath = memberId ? `members/${memberId}` : 'members';
        return this.__roomReference(chatRoomId).child(referencePath);
    }

    // Get a reference to the roomMessages sub-collection in a chat room
    static __messagesReference(chatRoomId) {
        /* chatRooms/{UUID}/roomMessages */
        return this.__roomReference(chatRoomId).child('roomMessages');
    }

    // Get a reference to the privateChats sub-collection in a chat room
    static __privateChatsReference(chatRoomId, chatId = null) {
        /* chatRooms/{UUID}/privateChats/{chatId} */
        const referencePath = chatId ? `privateChats/${chatId}` : 'privateChats';
        return this.__roomReference(chatRoomId).child(referencePath);
    }

    // Get a reference to the Rejected Profiles sub-collection in a chat room
    static __rejectedProfilesReference(chatRoomId, sender_id = null) {
        /* chatRooms/{UUID}/privateChats/{chatId} */
        const referencePath = `rejectedProfiles`;
        return this.__membersReference(chatRoomId,sender_id).child(referencePath);
    }

    // Get a reference to the Rejected Profiles sub-collection in a chat room
    static __memberFriendsListReference(chatRoomId, member_id) {
        /* chatRooms/{UUID}/privateChats/{chatId} */
        const referencePath = `acceptedProfiles`;
        return this.__membersReference(chatRoomId,member_id).child(referencePath)
    }

    // Get a reference to a private chat's messages
    static __privateChatMessagesReference(chatRoomId, chatId) {
        /* chatRooms/{UUID}/privateChats/{chatId}/messages */
        return this.__privateChatsReference(chatRoomId, chatId).child('messages');
    }

    // Get the data for a specific chat room by ID
    static async __getChatRoom(chatRoomId) {
        try {
            // Fetch the chat room data
            const snapshot = await this.safeExecuteAsync(() => this.__roomReference(chatRoomId).once('value'));
            
            // Check if the chat room exists
            if (!snapshot.exists()) {
                throw new Error(`Chat room with ID ${chatRoomId} not found.`);
            }

            return snapshot.val(); // Return the chat room data
        } catch (error) {
            console.error(`Error fetching chat room with ID ${chatRoomId}:`, error);
            throw error;
        }
    }

    // Get a list of all members by online status (true for online, false for offline)
    static async __getMembers(chatRoomId, status = null, excludeMember = null) {
        return this.safeExecuteAsync(async () => {
            let query = this.__membersReference(chatRoomId);
            
            // If status is provided, filter by onlineStatus
            if (status !== null) {
                query = query.orderByChild('onlineStatus').equalTo(status)
            }

            const snapshot = await query.once('value'); // Fetch the data

            if (!snapshot.exists()) {
                throw new Error(`No members found for the given status in chat room ${chatRoomId}`);
            }

            const data = snapshot.val();
            
            // Filter based on Exclude Member
            return Object.keys(data)
                .map(key => ({ id: key, ...data[key] }))
                .filter((member) => {
                    return excludeMember?(member.consumer_id !== excludeMember):(member!=null)
                });
        });
    }

    // Get a particluar member based on id
    static async __getMemberDetailBasedOnId(chatRoomId, id) {
        
        return this.safeExecuteAsync(async () => {
            const memberRef = await this.__membersReference(chatRoomId,id).once('value');
            
            if (!memberRef.exists()) {
                throw new Error('Member not found');
            }

            return memberRef.val()
        });
    }
    // Get a particluar member based on consumer_id
    static async __getMemberDetail(chatRoomId, consumer_id) {
        return this.safeExecuteAsync(async () => {
            const memberRef = await this.__membersReference(chatRoomId).orderByChild('consumer_id').equalTo(consumer_id).once('value');
            
            if (!memberRef.exists()) {
                throw new Error('Member not found');
            }

            return memberRef.val()
        });
    }

    // Set member's status to online
    static __setMemberOnline(chatRoomId, memberId) {
        return this.__membersReference(chatRoomId, memberId).update({
            onlineStatus: true, // Set online status to true
            lastSeen: null, // No lastSeen timestamp when online
        });
    }

    // Set member's Profile Image
    static __setMemberProfileImage(chatRoomId, memberId, profile_image) {
        return this.__membersReference(chatRoomId, memberId).update({
            profile_image, // Set online status to true
        });
    }

    // Set member's status to offline with lastSeen timestamp
    static __setMemberOffline(chatRoomId, memberId) {
        return this.__membersReference(chatRoomId, memberId).update({
            onlineStatus: false, // Set online status to false
            lastSeen: firebase.database.ServerValue.TIMESTAMP, // Set lastSeen timestamp
        });
    }

    // Add a message to the chat room
    static async __addMessage(chatRoomId, senderId, text, messageType = 'text', mediaUrl = null) {
        return this.safeExecuteAsync(async () => {
            const newMessageRef = this.__messagesReference(chatRoomId).push();  // Push a new message document

            const message = {
                senderId,
                text,
                messageType,
                timestamp: firebase.database.ServerValue.TIMESTAMP,  // Get the timestamp in UTC
                mediaUrl: mediaUrl || null,  // Set mediaUrl if provided
            };

            await newMessageRef.set(message);  // Store the message in the room's message collection
            return newMessageRef.key;  // Return the message ID
        });
    }

    // Get all messages in a chat room
    static async __getAllMessages(chatRoomId) {
        return this.safeExecuteAsync(async () => {
            const snapshot = await this.__messagesReference(chatRoomId).once('value');
            
            if (!snapshot.exists()) {
                throw new Error(`No messages found in chat room ${chatRoomId}`);
            }

            return snapshot.val();  // Return all messages in the room
        });
    }

    // Get a specific message in a chat room by messageId
    static async __getMessage(chatRoomId, messageId) {
        return this.safeExecuteAsync(async () => {
            const snapshot = await this.__messagesReference(chatRoomId).child(messageId).once('value');
            
            if (!snapshot.exists()) {
                throw new Error(`Message ${messageId} not found in chat room ${chatRoomId}`);
            }

            return snapshot.val();  // Return the message document
        });
    }

    // Get messages sent by a specific sender in a chat room
    static async __getMessagesBySender(chatRoomId, senderId) {
        return this.safeExecuteAsync(async () => {
            const snapshot = await this.__messagesReference(chatRoomId).orderByChild('senderId').equalTo(senderId).once('value');
            
            if (!snapshot.exists()) {
                throw new Error(`No messages found from sender ${senderId} in chat room ${chatRoomId}`);
            }

            return snapshot.val();  // Return the list of messages from the sender
        });
    }

    // Create a private chat between two members (if not already created)
    static async __createPrivateChat(chatRoomId, memberId1, memberId2) {
        return this.safeExecuteAsync(async () => {
            // Generate a unique chatId by sorting member IDs
            const chatId = [memberId1, memberId2].sort().join('_');

            const privateChatRef = this.__privateChatsReference(chatRoomId, chatId);

            // Check if the private chat already exists
            const snapshot = await privateChatRef.once('value');
            if (snapshot.exists()) {
                return chatId;  // Return existing chatId if already exists
            }

            // Create a new private chat if it doesn't exist
            await privateChatRef.set({
                participants: {
                    [memberId1]: true,
                    [memberId2]: true
                },
                isApproved:false,
                lastMessage: '',
                lastMessageTimestamp: null,
            });

            return chatId;  // Return the created chatId
        });
    }

    // Add a message to a private chat
    static async __addPrivateMessage(chatRoomId, chatId, senderId, receiverId, text, messageType = 'text', mediaUrl = null, approveStatus=null) {
        return this.safeExecuteAsync(async () => {
            const newMessageRef = this.__privateChatMessagesReference(chatRoomId, chatId).push();

            const message = {
                senderId,
                receiverId,
                text,
                messageType,
                timestamp: firebase.database.ServerValue.TIMESTAMP,  // UTC timestamp
                isRead:false,
                mediaUrl: mediaUrl || null,  // Optional media URL
            };

            if (approveStatus) {
                message.approved = (approveStatus == 'not_approved') ? false : true
            }

            // Add the message to the private chat
            await newMessageRef.set(message);

            // Update the last message and lastMessageTimestamp in the private chat reference
            await this.__privateChatsReference(chatRoomId, chatId).update({
                lastMessage: text,
                lastMessageTimestamp: firebase.database.ServerValue.TIMESTAMP,
            });

            // return newMessageRef.key;  // Return the message ID
            return message;  // Return the message
        });
    }

    // Get all messages in a private chat
    static async __getPrivateMessages(chatRoomId, chatId) {
        return this.safeExecuteAsync(async () => {
            const snapshot = await this.__privateChatMessagesReference(chatRoomId, chatId).once('value');

            if (!snapshot.exists()) {
                throw new Error(`No messages found in private chat ${chatId} in chat room ${chatRoomId}`);
            }

            return snapshot.val();  // Return all messages in the private chat
        });
    }

    // Get the most recent message in a private chat
    static async __getLastPrivateMessage(chatRoomId, chatId) {
        return this.safeExecuteAsync(async () => {
            const snapshot = await this.__privateChatsReference(chatRoomId, chatId).once('value');
            
            if (!snapshot.exists()) {
                throw new Error(`Private chat ${chatId} not found in chat room ${chatRoomId}`);
            }

            const privateChat = snapshot.val();
            return privateChat.lastMessage;  // Return the most recent message preview
        });
    }

    // Get all private chats for a member in a chat room
    static async __getAllPrivateChatsForMember(chatRoomId, memberId) {
        return this.safeExecuteAsync(async () => {
            const snapshot = await this.__privateChatsReference(chatRoomId).orderByChild('participants').equalTo(memberId).once('value');
            
            if (!snapshot.exists()) {
                throw new Error(`No private chats found for member ${memberId} in chat room ${chatRoomId}`);
            }

            return snapshot.val();  // Return all private chats for the member
        });
    }

    // Count unread messages for a member
    static async __countUnreadMessages(chatRoomId, memberId) {
        return this.safeExecuteAsync(async () => {
            const messagesRef = this.__messagesReference(chatRoomId);
            const snapshot = await messagesRef.orderByChild('receiverId').equalTo(memberId).once('value');
            
            let unreadCount = 0;
            snapshot.forEach(childSnapshot => {
                if (!childSnapshot.val().read) {
                    unreadCount++;
                }
            });

            return unreadCount;
        });
    }

    // Count unread messages in a Chat
    static async __countUnreadMessagesPrivateChat(chatRoomId, chatId, sender_id, receiver_id) {
        return this.safeExecuteAsync(async () => {
            // console.log('Receiver id: ',memberId);
            let unreadMessages = 0;
            const privateChatRef = await this.__privateChatsReference(chatRoomId, chatId).once('value');
              
            if (privateChatRef.exists()) {
                const allMessages = privateChatRef.val();
                unreadMessages = Object.values(allMessages.messages)
                .filter(message => 
                    message.senderId === sender_id && message.receiverId === receiver_id && message.isRead === false
                ).length;
                
            }

            return unreadMessages
        });
    }

    // Approve/reject the first message between two users
    static async __approveRejectFirstMessage(chatRoomId, memberId1, memberId2, action) {
        return this.safeExecuteAsync(async () => {
            const chatId = [memberId1, memberId2].sort().join('_');
            const privateChatRef = this.__privateChatMessagesReference(chatRoomId, chatId);

            const snapshot = await privateChatRef.once('value');
            const messages = snapshot.val();
            
            if (!messages) {
                throw new Error('No messages found for the private chat');
            }

            let member1Details = await this.__getMemberDetailBasedOnId(chatRoomId, memberId1);
            let member2Details = await this.__getMemberDetailBasedOnId(chatRoomId, memberId2);
            
            /**
             * If User Accepts The Message
             */
            if (action === 'approve') {
                // Get The First message
                const firstMessage = Object.entries(messages).map(([messageID, messageData]) => ({
                    messageID,
                    ...messageData
                }))[0];

                // Update the status of first message 
                const updatedMessage = {
                    ...firstMessage,
                    approved: true,
                };
                await privateChatRef.child(firstMessage.messageID).update(updatedMessage);
                
                /** If Users Approved
                 * Then Each user can be added into the friends list of each other
                 */

                let member1FriendRef = this.__memberFriendsListReference(chatRoomId,member1Details.id).push()
                await member1FriendRef.set(member2Details)

                let member2FriendRef = this.__memberFriendsListReference(chatRoomId,member2Details.id).push()
                await member2FriendRef.set(member1Details)
                
                console.log("Approved chat");
                
                await this.__privateChatsReference(chatRoomId, chatId).update({
                    isApproved:true
                });

                return updatedMessage; // Return the updated message
            }else{
                await this.__rejectProfile(chatRoomId, member1Details.id, member2Details.id)
            }
        });
    }

    static async __rejectProfile(chatRoomId, sender_id, rejected_profile_id) {
        return this.safeExecuteAsync(async () => {

            const rejecteMmemberDetail = await this.__getMemberDetailBasedOnId(chatRoomId,rejected_profile_id);
            
            if (!rejecteMmemberDetail) {
                throw new Error('Rejected Member not found');
            }
            
            let senderRejectedRef = this.__rejectedProfilesReference(chatRoomId,sender_id).push()
            await senderRejectedRef.set(rejecteMmemberDetail)
            
            
            const senderMemberDetail = await this.__getMemberDetailBasedOnId(chatRoomId,sender_id);
            
            if (!senderMemberDetail) {
                throw new Error('Rejected Member not found');
            }

            let rejectedDocf = this.__rejectedProfilesReference(chatRoomId,rejected_profile_id).push()
            await rejectedDocf.set(senderMemberDetail)

            // Set Whole Chat as Rejected
            const chatId = [rejecteMmemberDetail.consumer_id, senderMemberDetail.consumer_id].sort().join('_');

            // Update the rejected Flag in the private chat reference
            await this.__privateChatsReference(chatRoomId, chatId).update({
                isRejected: true
            });
        })
    }

    // Update member status (e.g., online, offline, typing)
    static async __updateMemberStatus(chatRoomId, memberId, activityStatus) {
        return this.safeExecuteAsync(async () => {
            const memberRef = this.__membersReference(chatRoomId, memberId);
            await memberRef.update({
                onlineStatus: activityStatus,
                lastActive: firebase.database.ServerValue.TIMESTAMP,
            });
        });
    }

    // Add reaction to a message Old Method
    static async __addReactionToMessageOld(chatRoomId, messageId, reaction) {
        return this.safeExecuteAsync(async () => {
            const messageRef = this.__messagesReference(chatRoomId).child(messageId);
            await messageRef.child('reactions').push(reaction);  // Store reactions in a sub-collection
        });
    }

    static async __addReactionToMessage(chatRoomId, chatUniqueId, messageId, reaction) {
        return this.safeExecuteAsync(async () => {
            const messageRef = this.__privateChatMessagesReference(chatRoomId,chatUniqueId).child(messageId);
            await messageRef.child('reactions').remove() // remove Store reactions in a sub-collection
            await messageRef.child('reactions').push(reaction); // Store reactions in a sub-collection
        });
    }
    static async __removeReactionFromMessage(chatRoomId, chatUniqueId, messageId) {
        return this.safeExecuteAsync(async () => {
            const messageRef = this.__privateChatMessagesReference(chatRoomId,chatUniqueId).child(messageId);
            await messageRef.child('reactions').remove();  // Store reactions in a sub-collection
        });
    }

    // Get reaction of a message
    static async __getMessageReaction(chatRoomId, messageId) {
        return this.safeExecuteAsync(async () => {
            const snapshot = await this.__messagesReference(chatRoomId)
                .child(messageId)
                .child('reactions')
                .once('value');
            if (snapshot.exists()) {
                return snapshot.val();  // Return all private chats for the member
            }else{
                return null;
            }
        });
    }

    // Edit a message
    static async __editMessage(chatRoomId, messageId, newText) {
        return this.safeExecuteAsync(async () => {
            const messageRef = this.__messagesReference(chatRoomId).child(messageId);
            await messageRef.update({ text: newText, edited: true });
        });
    }

    // Delete a message
    static async __deleteMessage(chatRoomId, messageId) {
        return this.safeExecuteAsync(async () => {
            const messageRef = this.__messagesReference(chatRoomId).child(messageId);
            await messageRef.remove();
        });
    }

    // Forward a message to another chat
    static async __forwardMessage(chatRoomId, messageId, targetChatRoomId) {
        return this.safeExecuteAsync(async () => {
            const messageRef = this.__messagesReference(chatRoomId).child(messageId);
            const messageSnapshot = await messageRef.once('value');
            
            if (!messageSnapshot.exists()) {
                throw new Error('Message not found');
            }

            const messageData = messageSnapshot.val();
            const newMessageRef = this.__messagesReference(targetChatRoomId).push();
            await newMessageRef.set(messageData);
        
            return newMessageRef.key;  // Return forwarded message ID
        });
    }

    // Get last message from a chat room
    static async __getLastMessage(chatRoomId) {
        return this.safeExecuteAsync(async () => {
            const snapshot = await this.__messagesReference(chatRoomId).limitToLast(1).once('value');
            
            if (!snapshot.exists()) {
                throw new Error('No messages found in chat room');
            }

            const lastMessage = Object.values(snapshot.val())[0];
            return lastMessage;
        });
    }

    // Get the last active time for a member
    static async __getLastActiveTime(chatRoomId, memberId) {
        return this.safeExecuteAsync(async () => {
            const memberRef = this.__membersReference(chatRoomId, memberId);
            const snapshot = await memberRef.once('value');

            if (!snapshot.exists()) {
                throw new Error('Member not found');
            }

            return snapshot.val().lastActive;  // Return the last active timestamp
        });
    }
    
    static async setChatMessagesAsRead(chatRoomId, chatId){
        return this.safeExecute(async () => {
            const privateChatRefSnapshot = await this.__privateChatsReference(chatRoomId, chatId).child('messages').orderByChild('isRead').equalTo(false).once('value')
            if (!privateChatRefSnapshot.exists()) {
                throw new Error(`No unread message is chat room ${chatId}`);
            }

            let unreadMessage = await privateChatRefSnapshot.val();

            let unReadChatMesages = Object.keys(unreadMessage)
            .map(key => ({ id: key, ...unreadMessage[key] }))

            if (unReadChatMesages.length > 0 ) {
                const privateChatRef = this.__privateChatMessagesReference(chatRoomId, chatId);
                unReadChatMesages.forEach(async(element) => {
                    await privateChatRef.child(element.id).update({isRead:true});
                })
            }
            // console.log(unReadChatMesages);
            
        })
    }

/**--------------------------------------------------------------------------------------------------- */
    // Listening Methods
    
    // Listen to The Added Members
    static listenToAddedMembers(chatRoomId, callback) {
        const messagesRef = this.__membersReference(chatRoomId);
        messagesRef.on('child_added', (snapshot) => {
            const newMessage = snapshot.val();
            callback(newMessage); // Push the new Members to the UI
        });
    }
    
    // Listen to The Private User Chat List
    static async listenAllChatsForMember(chatRoomId, memberId, callback){
        const snapshotRef = this.__memberFriendsListReference(chatRoomId, memberId)
        snapshotRef.on('value', (snapshot) => {
            if (snapshot.exists()) {
                callback(snapshot.val())
            }else {
                throw new Error("Snapshot does not exists");
                
            }
        })
    }
    // Listen to The Private User Chat List
    static async stoplisteningAllChatsForMember(chatRoomId, memberId, callback){
        const snapshotRef = this.__memberFriendsListReference(chatRoomId, memberId)
        snapshotRef.off();
    }

    // Stop Listen To the Added Memeers
    static stopListeningToAddedMembers(chatRoomId) {
        const messagesRef = this.__membersReference(chatRoomId);
        messagesRef.off(); // Turn off all listeners for the messages reference
    }

    static listenToMemberProfileChange(chatRoomId, callback){
        const messagesRef = this.__membersReference(chatRoomId);
        messagesRef.on('value', (snapshot) => {
            if (snapshot.exists()) {
                const status = snapshot.val();
                callback(status); // Notify about the approval/rejection status
            }
        });
    }

    static listenToAddedMessages(chatRoomId, chatId, callback) {
        const messagesRef = this.__privateChatMessagesReference(chatRoomId, chatId);
        messagesRef.on('child_added', (snapshot) => {
            const newMessage = {
                id: snapshot.key,
                ...snapshot.val()
            }
            callback(newMessage); // Push the new message to the UI
        });
    }
    
    static listenToPrivateChatMessages(chatRoomId, chatId, callback) {
        const messagesRef = this.__privateChatMessagesReference(chatRoomId, chatId);
        messagesRef.on('value', (snapshot) => {
            const messages = snapshot.val()
            callback(messages); // Push the new message to the UI
        });
    }

    static listenToEditedMessages(chatRoomId, chatId, callback) {
        const messagesRef = this.__privateChatMessagesReference(chatRoomId, chatId);
        messagesRef.on('child_changed', (snapshot) => {
            const updatedMessage = snapshot.val();
            // const messageId = snapshot.key;
            callback(updatedMessage); // Notify the UI of the updated message
        });
    }

    static listenToDeletedMessages(chatRoomId, chatId, callback) {
        const messagesRef = this.__privateChatMessagesReference(chatRoomId, chatId);
        messagesRef.on('child_removed', (snapshot) => {
            const deletedMessage = snapshot.val();
            callback(deletedMessage); // Notify the UI of the deleted message
        });
    }

    static stopListeningToPrivateMessages(chatRoomId, chatId) {
        const messagesRef = this.__privateChatMessagesReference(chatRoomId, chatId);
        messagesRef.off(); // Turn off all listeners for the messages reference
    }

    static stopListeningToAddedMessages(chatRoomId, chatId) {
        const messagesRef = this.__privateChatMessagesReference(chatRoomId, chatId);
        messagesRef.off('child_added');
    }
    
    static stopListeningToEditedMessages(chatRoomId, chatId) {
        const messagesRef = this.__privateChatMessagesReference(chatRoomId, chatId);
        messagesRef.off('child_changed');
    }
    
    static stopListeningToDeletedMessages(chatRoomId, chatId) {
        const messagesRef = this.__privateChatMessagesReference(chatRoomId, chatId);
        messagesRef.off('child_removed');
    }

    static listenToLastPrivateMessage(chatRoomId, chatId, callback) {
        const chatRef = this.__privateChatsReference(chatRoomId, chatId);
        chatRef.on('value', (snapshot) => {
            if (snapshot.exists()) {
                const lastMessage = snapshot.val().lastMessage;
                callback(lastMessage); // Send the latest message to the UI
            }
        });
    }

    static listenToPrivateChatsForMember(chatRoomId, memberId, callback) {
        const chatsRef = this.__privateChatsReference(chatRoomId);
        chatsRef.orderByChild(`participants/${memberId}`).equalTo(true).on('value', (snapshot) => {
            const chat = snapshot.val();
            callback(chat); // Send new or updated private chats to the UI
        });
    }

    static listenToUnreadMessages(chatRoomId, memberId, callback) {
        const messagesRef = this.__messagesReference(chatRoomId);
        messagesRef.orderByChild('receiverId').equalTo(memberId).on('child_added', (snapshot) => {
            const message = snapshot.val();
            if (!message.read) {
                callback(message); // Notify about the unread message
            }
        });
    }

    static listenToApprovalStatus(chatRoomId, chatId, messageId, callback) {
        const messageRef = this.__privateChatMessagesReference(chatRoomId, chatId).child(messageId);
        messageRef.on('value', (snapshot) => {
            if (snapshot.exists()) {
                const status = snapshot.val().approved;
                callback(status); // Notify about the approval/rejection status
            }
        });
    }

    static listenToMemberStatus(chatRoomId, memberId, callback) {
        const memberRef = this.__membersReference(chatRoomId, memberId);
        memberRef.on('value', (snapshot) => {
            if (snapshot.exists()) {
                const status = snapshot.val();
                callback(status); // Send the updated status
            }
        });
    }

    static listenToMessageReactions(chatRoomId, messageId, callback) {
        const reactionsRef = this.__messagesReference(chatRoomId).child(messageId).child('reactions');
        reactionsRef.on('child_added', (snapshot) => {
            callback({
                message_id:messageId,
                reaction:snapshot.val()
            }); // Send the new reaction to the UI
        });
    }

    static listenToLastMessage(chatRoomId, callback) {
        const chatRoomRef = this.__chatRoomReference(chatRoomId);
        chatRoomRef.on('value', (snapshot) => {
            const lastMessage = snapshot.val() ? snapshot.val().lastMessage : null;
            callback(lastMessage); // Send the latest last message to the UI
        });
    }

    static listenToLastActiveTime(chatRoomId, memberId, callback) {
        const memberRef = this.__membersReference(chatRoomId, memberId);
        memberRef.on('value', (snapshot) => {
            const lastActiveTime = snapshot.val() ? snapshot.val().lastActive : null;
            callback(lastActiveTime); // Notify the UI of the member's last active time
        });
    }

    static listenToRejectedProfiles(chatRoomId, memberId, callback) {
        const memberRef = this.__rejectedProfilesReference(chatRoomId, memberId);
        memberRef.on('value', (snapshot) => {
            const rejectedProfile = snapshot.val();
            callback(rejectedProfile); // Notify the UI of the member's last active time
        });
    }
    static stoplisteningToRejectedProfiles(chatRoomId, memberId) {
        const currentUserProfileRef = this.__rejectedProfilesReference(chatRoomId, memberId);
        currentUserProfileRef.off(); // Turn off all listeners for the messages reference
    }

    static listenToMemberProfile(chatRoomId, memberId, callback) {
        const memberRef = this.__membersReference(chatRoomId, memberId);
        memberRef.on('value', (snapshot) => {
            callback(snapshot.val()); // Notify the UI of the member's last active time
        });
    }

    static stoplisteningToMemberProfile(chatRoomId, memberId){
        const currentUserProfileRef = this.__membersReference(chatRoomId, memberId);
        currentUserProfileRef.off(); // Turn off all listeners for the messages reference
    }

    static listenToPrivateChat(chatRoomId, chatId, callback){
        const chatsRef = this.__privateChatsReference(chatRoomId,chatId);
        chatsRef.on('value', (snapshot) => {
            callback(snapshot.val()); // Notify the UI of the member's last active time
        });
    }
    
    static stoplisteningToPrivateChat(chatRoomId, chatId){
        const chatsRef = this.__privateChatsReference(chatRoomId, chatId);
        chatsRef.off(); // Turn off all listeners for the messages reference
    }


}

export default ChatRoomService;
